import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import type { ReactNode } from "react"
import {
  autoUpdate,
  size,
  flip,
  offset,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole,
  FloatingFocusManager,
  FloatingList
} from "@floating-ui/react"
import cx from "classnames"
import { debounce } from "lodash"
import Label from "../label"
import Icon from "../icon"
import Portal from "../portal"
import {
  SelectContext,
  MENU_CLASSES,
  MAX_MENU_HEIGHT,
  MENU_OFFSET
} from "./select"
import type { SelectOption } from "./select"
import Option from "./option"
import { jsxTextContent } from "./util"

const DEFAULT_INPUT_CLASSES =
  "relative inline-flex items-center border transition-colors delay-75 text-sm gap-2 rounded h-10"

type FilteredOption = {
  label: ReactNode
  value: string | number
  position: number
}

export type FilterFn = (
  options: FilteredOption[],
  inputValue: string,
  selectedIndex: number
) => FilteredOption[]

export type AsyncOptionsFn = (
  input: string,
  opts: { signal: AbortSignal }
) => Promise<SelectOption[]>

export type AutocompleteChangeTarget = {
  label: ReactNode
  value: string | number
  name?: string
  position: number
  created?: boolean
}

const DEFAULT_FILTER: FilterFn = (options, inputValue, selectedIndex) => {
  const input = inputValue.toLowerCase()
  if (!input) return options
  if (selectedIndex !== -1) return options
  const result = options.filter(option => {
    const labelString = jsxTextContent(option.label)
    if (!labelString) return false

    return labelString.toLowerCase().includes(input)
  })
  return result
}

export type AutoCompleteProps = {
  /** Whether options are loaded asynchronously */
  async?: boolean
  /** Currently selected value, or a prefilled {label, value} for async mode */
  value?: string | number | { label: string; value: string }
  /** Options array, or an async function that returns options */
  options: SelectOption[] | AsyncOptionsFn
  /** Called when a new option is selected */
  onChange?: (event: { target: AutocompleteChangeTarget }) => void
  /** Minimum characters before triggering async load */
  minCharsToLoad?: number
  /** Additional CSS classes */
  className?: string
  /** When true, removes border and uses tertiary button styling */
  borderless?: boolean
  /** Visible label displayed above the input */
  label?: string
  /** Accessible label for screen readers */
  "aria-label"?: string
  /** ID of an external element that labels this input */
  "aria-labelledby"?: string
  /** Name attribute, included in the onChange event target */
  name?: string
  /** Whether the input is in an error state */
  error?: boolean
  /** Whether the input is disabled */
  disabled?: boolean
  /** Text shown when no option is selected */
  placeholder?: string
  /** Whether to hide the dropdown caret icon */
  hideCaretIcon?: boolean
  /** Allows adding new options. When a string, overrides the default "Create" prefix */
  create?: boolean | string
  /** Custom filter function for options */
  filter?: FilterFn
}

export type ComposableAutocompleteProps = {
  /** Option elements to render in the dropdown */
  children?: ReactNode
  /** Additional CSS classes */
  className?: string
  /** Visible label displayed above the input */
  label?: string
  /** Controlled search/input value */
  searchValue?: string
  /** Text shown when no option is selected */
  placeholder?: string
  /** Custom filter function for options */
  filter?: FilterFn
  /** Allows adding new options. When a string, overrides the default "Create" prefix */
  create?: boolean | string
  /** External loading/state indicator */
  state?: { loading?: boolean }
  /** Whether the input is disabled */
  disabled?: boolean
  /** Called when the input value changes */
  onFilter?: (value: string, filteredOptions?: FilteredOption[]) => void
  /** Whether to hide the dropdown caret icon */
  hideCaretIcon?: boolean
  /** When true, removes border and uses tertiary button styling */
  borderless?: boolean
  /** Accessible label for screen readers */
  "aria-label"?: string
  /** ID of an external element that labels this input */
  "aria-labelledby"?: string
  /** Name attribute, included in the onChange event target */
  name?: string
  /** Whether the input is in an error state */
  error?: boolean
  /** Called when a new option is selected */
  onChange?: (event: { target: AutocompleteChangeTarget }) => void
}

const ComposableAutocomplete = forwardRef<
  HTMLInputElement,
  ComposableAutocompleteProps
>(
  (
    {
      children,
      className,
      label,
      searchValue,
      placeholder = "Select",
      filter = DEFAULT_FILTER,
      create,
      state = {},
      disabled,
      onFilter,
      hideCaretIcon = false,
      borderless = false,
      ...props
    },
    forwardedRef
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const childNodes = useMemo(
      () => React.Children.toArray(children) as React.ReactElement[],
      [children]
    )
    const [inputValue, setInputValue] = useState(() => {
      const index = childNodes.findIndex(child => child.props.selected)
      if (index < 0) return searchValue || ""

      const selectedLabel =
        childNodes[index].props.label || childNodes[index].props.children
      return searchValue || selectedLabel || ""
    })
    const selectedIndex = childNodes.findIndex(child => {
      const labelString = jsxTextContent(
        child.props.label || child.props.children
      )
      return labelString === inputValue
    })
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const createPrefix = typeof create === "string" ? create : "Create"

    // Allows to set the input value from the outside
    useEffect(() => {
      if (typeof searchValue === "string") {
        setInputValue(searchValue)
      }
    }, [searchValue])

    const filteredOptions = filter(
      childNodes.map((child, index) => ({
        label: child.props.label || child.props.children,
        value: child.props.value,
        position: index,
        ...child.props.customKeys
      })),
      inputValue,
      selectedIndex
    )
    const labelId = useId()
    const inputId = useId()

    const elementsRef = useRef<(HTMLElement | null)[]>([])

    const { refs, floatingStyles, context } = useFloating({
      placement: "bottom-start",
      open: isOpen,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
        flip(),
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              minWidth: `${rects.reference.width}px`
            })
          }
        }),
        offset(MENU_OFFSET)
      ]
    })

    const role = useRole(context, { role: "listbox" })
    const dismiss = useDismiss(context)
    const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      onNavigate: setActiveIndex,
      virtual: true,
      loop: true
    })

    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions([role, dismiss, listNav])

    const handleSelect = useCallback(
      (index: number) => {
        const createdOption = {
          label: inputValue,
          value: inputValue,
          position: childNodes.length,
          created: true
        }
        const selectedOption = filteredOptions[index] || createdOption
        if (!selectedOption) return
        const labelString = jsxTextContent(selectedOption.label)
        setInputValue(labelString)
        if (props.onChange) {
          props.onChange({ target: { ...selectedOption, name: props.name } })
        }
        setIsOpen(false)
        ;(refs.domReference.current as HTMLInputElement | null)?.focus()
      },
      [filteredOptions, refs.domReference, props, inputValue, childNodes]
    )

    const selectContext = useMemo(
      () => ({
        activeIndex,
        selectedIndex,
        getItemProps,
        handleSelect,
        inputValue
      }),
      [activeIndex, selectedIndex, getItemProps, handleSelect, inputValue]
    )

    const onChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setInputValue(value)
        if (!isOpen) setIsOpen(true)
        const newFilteredOptions = filter(
          childNodes.map((child, index) => ({
            label: child.props.label || child.props.children,
            value: child.props.value,
            position: index
          })),
          value,
          selectedIndex
        )

        if (value) {
          setActiveIndex(0)
        }
        if (onFilter) {
          onFilter(value, newFilteredOptions)
        }
      },
      [isOpen, onFilter, childNodes, filter, selectedIndex]
    )

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && activeIndex != null) {
          /*
          We are using a virtual navigation to keep focus on the input (see useListNavigation hook above)
          With a virtual navigation, interactions like Enter will not trigger selection/click/onchange.
          Hence, we need to call handleSelect below to trigger onChange callback during the input keydown event.
        */
          handleSelect(activeIndex)
        }
      },
      [activeIndex, handleSelect]
    )

    const onClick = useCallback(() => {
      if (!isOpen) (refs.reference.current as HTMLInputElement).select()

      setIsOpen(!isOpen)
    }, [isOpen, refs.reference])

    const inputClassName = cx(DEFAULT_INPUT_CLASSES, className, {
      "text-gray-600": !inputValue,
      "text-gray-900": inputValue,
      "border-blue-500": isOpen && !props.error && !borderless,
      "border-gray-200 hover:border-gray-300":
        !isOpen && !props.error && !borderless,
      "border-none bg-transparent hover:bg-gray-900/[0.06] focus:bg-gray-900/[0.06] cursor-pointer":
        borderless,
      "border-red-500": props.error,
      "bg-gray-50": disabled,
      "bg-gray-900/[0.06]": borderless && isOpen
    })

    return (
      <>
        {label && (
          <Label className="cursor-default" id={labelId} htmlFor={inputId}>
            {label}
          </Label>
        )}
        <div className={inputClassName}>
          <input
            {...getReferenceProps({
              id: inputId,
              ref: useMergeRefs([refs.setReference, forwardedRef]),
              onChange,
              onClick,
              onKeyDown,
              value: inputValue,
              placeholder,
              "aria-autocomplete": "list"
            })}
            aria-labelledby={label ? labelId : props["aria-labelledby"]}
            aria-label={!label ? props["aria-label"] : undefined}
            className="border-none w-full h-full bg-transparent focus:outline-none pl-3 pr-12 placeholder-gray-600"
            style={{ minWidth: placeholder.length + 2 + "ch" }}
            disabled={disabled}
          />
          {!hideCaretIcon && (
            <Icon
              name="down"
              className={cx(
                "absolute right-0 mr-2 w-6 duration-75 transition-transform pointer-events-none transform text-gray-800",
                { "rotate-180": isOpen }
              )}
              aria-hidden="true"
            />
          )}
        </div>
        <SelectContext.Provider value={selectContext}>
          {isOpen && (
            <FloatingFocusManager
              context={context}
              initialFocus={-1}
              modal={false}
            >
              <Portal>
                <div
                  ref={refs.setFloating}
                  style={{
                    ...floatingStyles,
                    maxHeight: MAX_MENU_HEIGHT
                  }}
                  className={MENU_CLASSES}
                  aria-label="Options"
                  {...getFloatingProps()}
                >
                  <FloatingList elementsRef={elementsRef}>
                    {filteredOptions.length === 0 && !state.loading && (
                      <Option disabled className="text-center text-gray-600">
                        No result found
                      </Option>
                    )}
                    {filteredOptions.length === 0 && state.loading && (
                      <Option disabled className="text-center text-gray-600">
                        Loading...
                      </Option>
                    )}
                    {filteredOptions.length > 0 &&
                      React.Children.map(children, (child, index) => {
                        const isFilteredOut = !filteredOptions.find(
                          option => option.position === index
                        )
                        return isFilteredOut ? null : child
                      })}
                    {create &&
                      inputValue.length > 1 &&
                      !filteredOptions.find(
                        option =>
                          jsxTextContent(option.label).toLowerCase() ===
                          inputValue.toLowerCase()
                      ) && (
                        <Option create>
                          {createPrefix} &ldquo;{inputValue}&rdquo;
                        </Option>
                      )}
                  </FloatingList>
                </div>
              </Portal>
            </FloatingFocusManager>
          )}
        </SelectContext.Provider>
      </>
    )
  }
)

const AutoComplete = forwardRef<HTMLInputElement, AutoCompleteProps>(
  (
    { async, value, options: items, onChange, minCharsToLoad = 2, ...props },
    ref
  ) => {
    if (async && typeof items !== "function") {
      throw new Error("Async autocomplete requires options to be a function")
    }
    const [options, setOptions] = useState(
      async ? [] : (items as SelectOption[])
    )
    const [loading, setLoading] = useState(false)
    const [inputValue, setInputValue] = useState(() => {
      const selectedObject = options.find(option => option.value === value)
      const prefilled = typeof value === "object" ? value : null
      const selectedLabel = prefilled?.label || selectedObject?.label
      const valueString =
        typeof selectedLabel === "string"
          ? selectedLabel
          : (selectedLabel as React.ReactElement)?.props?.children

      return valueString || ""
    })

    const handleFilter = (filterString: string) => {
      setInputValue(filterString)
    }

    const handleChange = (event: { target: AutocompleteChangeTarget }) => {
      if (event.target.created)
        setOptions([...options, event.target as SelectOption])
      if (onChange) onChange(event)
    }

    const loadOptions = useCallback(
      (input: string) => {
        if (input.length < minCharsToLoad) return
        if (typeof items !== "function") return
        const abortController = new AbortController()
        setLoading(true)
        items(input, { signal: abortController.signal })
          .then(opts => {
            if (opts) setOptions(opts)
          })
          .catch(error => {
            if (error.name === "AbortError") {
              return
            }
            throw error
          })
          .finally(() => {
            setLoading(false)
          })
      },
      [items, minCharsToLoad]
    )

    const debouncedLoadOptions = useMemo(
      () => debounce(loadOptions, 300),
      [loadOptions]
    )

    useEffect(() => {
      if (!async) return
      debouncedLoadOptions(inputValue)
    }, [async, debouncedLoadOptions, inputValue])

    return (
      <ComposableAutocomplete
        {...props}
        ref={ref}
        searchValue={inputValue}
        state={{ loading }}
        onFilter={handleFilter}
        onChange={handleChange}
      >
        {options.map(option => {
          const {
            value,
            label,
            component,
            disabled = false,
            ...customKeys
          } = option
          return (
            <Option
              key={option.key || `${value}-${label}`}
              value={value}
              label={label}
              disabled={disabled}
              customKeys={customKeys}
            >
              {component || label}
            </Option>
          )
        })}
      </ComposableAutocomplete>
    )
  }
)

export { AutoComplete, ComposableAutocomplete }
